import { fromBinary, toBinary } from "@bufbuild/protobuf";
import { ClientMessageSchema, ServerMessageSchema } from "../proto/gunrun/v1/game_pb";
import type { ClientMessage, ServerMessage } from "../proto/gunrun/v1/game_pb";
import type { DecodedUnreliable, InputCmdData, SnapshotData } from "../wasm/types";
import { probe } from "../perf/probe";
import { IDLE_TIMEOUT_MS, LEAD_WINDOW, PING_INTERVAL_MS, SLACK_PER_TICK, TICK_MS, wholeTicks } from "./constants";
import { ConnectionClock, LossMeter, PerMinuteCounter, RateMeter } from "./stats";
import type { Transport, TransportKind, UnreliableLane } from "./transport";

/** Live numbers for the net graph (`Hud.netGraph`). Updated in place. */
export interface NetStats {
  /** "WT" or "WS". */
  transport: string;
  pingMs: number;
  jitterMs: number;
  /** Snapshot loss, percent (gaps in the snapshot ticks). */
  lossPct: number;
  /** Average `inputSlack` of the last 1/6 s of snapshots (ms): how far ahead of the server's need the inputs arrive. */
  inputLeadMs: number;
  /** Interpolation delay in ms; written by the game from `SnapshotBuffer.interpDelayMs()`. */
  interpMs: number;
  correctionsPerMin: number;
  lastCorrectionM: number;
  kbpsIn: number;
  kbpsOut: number;
}

/** The unreliable-channel codec (WASM in the game, a fake in tests). */
export interface UnreliableCodec {
  encodeInputPacket(lastSnapshotTick: number, cmds: readonly InputCmdData[]): Uint8Array;
  encodePing(clientTimeMs: number): Uint8Array;
  /** Throws on malformed bytes. */
  decode(bytes: Uint8Array): DecodedUnreliable;
}

export interface ConnectionEnv {
  now(): number;
  setInterval(fn: () => void, ms: number): number;
  clearInterval(handle: number): void;
}

// `Number()` keeps the handle a plain number in browsers and in Node (whose timers coerce to their id).
const realEnv: ConnectionEnv = {
  now: () => performance.now(),
  setInterval: (fn, ms) => Number(globalThis.setInterval(fn, ms)),
  clearInterval: (handle) => globalThis.clearInterval(handle),
};

/** Most snapshots (0.27 s) queued before the owner assigns `onSnapshot` (only the newest matter). */
const MAX_PENDING_SNAPSHOTS = wholeTicks(0.27);
const MAX_PENDING_MESSAGES = 256;

interface PendingSnapshot {
  s: SnapshotData;
  arrivalMs: number;
}

/**
 * One live connection to a game room: reliable protobuf messages, the unreliable snapshot/input
 * stream, the ping loop and the clock/loss/bandwidth statistics. Created by `connectGame`, which
 * has already sent `Hello` and started the ping loop. Anything that arrives before a handler is
 * assigned (the `Welcome`, the first snapshots) is queued and delivered on assignment.
 */
export class GameConnection {
  readonly transportKind: TransportKind;
  readonly clock: ConnectionClock;
  readonly stats: NetStats;

  private messageHandler: ((msg: ServerMessage) => void) | null = null;
  private snapshotHandler: ((s: SnapshotData, arrivalMs: number) => void) | null = null;
  private closeHandler: ((reason: string) => void) | null = null;
  private pendingMessages: ServerMessage[] = [];
  private pendingSnapshots: PendingSnapshot[] = [];
  private pendingClose: string | null = null;

  private readonly loss = new LossMeter();
  private readonly rateIn = new RateMeter();
  private readonly rateOut = new RateMeter();
  private readonly corrections = new PerMinuteCounter();
  private readonly leads = new Float64Array(LEAD_WINDOW);
  private leadCount = 0;
  private leadNext = 0;

  private timer: number | null = null;
  private lastRecvMs = 0;
  private kickReason: string | null = null;
  private closed = false;
  /** Datagrams and frames that could not be decoded. */
  malformed = 0;

  constructor(
    private readonly transport: Transport,
    private readonly codec: UnreliableCodec,
    private readonly env: ConnectionEnv = realEnv,
  ) {
    this.transportKind = transport.kind;
    this.clock = new ConnectionClock();
    this.stats = {
      transport: transport.kind === "webtransport" ? "WT" : "WS",
      pingMs: 0,
      jitterMs: 0,
      lossPct: 0,
      inputLeadMs: 0,
      interpMs: 0,
      correctionsPerMin: 0,
      lastCorrectionM: 0,
      kbpsIn: 0,
      kbpsOut: 0,
    };
    transport.onReliable = (payload) => this.receiveReliable(payload);
    transport.onUnreliable = (payload) => this.receiveUnreliable(payload);
    transport.onClose = (reason) => this.fail(reason);
  }

  // ---- handlers (queue until assigned) ----

  get onMessage(): (msg: ServerMessage) => void {
    return this.messageHandler ?? (() => undefined);
  }

  set onMessage(handler: (msg: ServerMessage) => void) {
    this.messageHandler = handler;
    const queued = this.pendingMessages;
    this.pendingMessages = [];
    for (const m of queued) handler(m);
  }

  get onSnapshot(): (s: SnapshotData, arrivalMs: number) => void {
    return this.snapshotHandler ?? (() => undefined);
  }

  set onSnapshot(handler: (s: SnapshotData, arrivalMs: number) => void) {
    this.snapshotHandler = handler;
    const queued = this.pendingSnapshots;
    this.pendingSnapshots = [];
    for (const q of queued) handler(q.s, q.arrivalMs);
  }

  get onClose(): (reason: string) => void {
    return this.closeHandler ?? (() => undefined);
  }

  set onClose(handler: (reason: string) => void) {
    this.closeHandler = handler;
    if (this.pendingClose !== null) {
      const reason = this.pendingClose;
      this.pendingClose = null;
      handler(reason);
    }
  }

  // ---- lifecycle ----

  /** Starts the ping loop (the first ping goes out immediately). */
  start(): void {
    this.lastRecvMs = this.env.now();
    this.pingTick();
    this.timer = this.env.setInterval(() => this.pingTick(), PING_INTERVAL_MS);
  }

  /** Closes the connection without calling `onClose`. */
  close(): void {
    if (this.closed) return;
    this.closed = true;
    this.stopTimer();
    this.transport.close();
  }

  get isClosed(): boolean {
    return this.closed;
  }

  private stopTimer(): void {
    if (this.timer !== null) this.env.clearInterval(this.timer);
    this.timer = null;
  }

  private fail(transportReason: string): void {
    if (this.closed) return;
    this.closed = true;
    this.stopTimer();
    this.transport.close();
    const reason = this.kickReason ?? transportReason;
    if (this.closeHandler) this.closeHandler(reason);
    else this.pendingClose = reason;
  }

  private pingTick(): void {
    const now = this.env.now();
    if (now - this.lastRecvMs > IDLE_TIMEOUT_MS) {
      this.fail("Connection timed out");
      return;
    }
    this.sendRaw(this.codec.encodePing(Math.floor(now)), now, "ping");
    this.stats.kbpsIn = this.rateIn.kbps(now);
    this.stats.kbpsOut = this.rateOut.kbps(now);
    this.stats.correctionsPerMin = this.corrections.perMinute(now);
  }

  private sendRaw(bytes: Uint8Array, now: number, lane: UnreliableLane): void {
    this.transport.sendUnreliable(bytes, lane);
    this.rateOut.add(bytes.length, now);
  }

  // ---- sending ----

  /** Sends a reliable message. */
  send(msg: ClientMessage): void {
    if (this.closed) return;
    const bytes = toBinary(ClientMessageSchema, msg);
    this.transport.sendReliable(bytes);
    this.rateOut.add(bytes.length, this.env.now());
  }

  /** Sends one input packet: the newest input last, older unacknowledged ones before it. */
  sendInputs(lastSnapshotTick: number, cmds: readonly InputCmdData[]): void {
    if (this.closed) return;
    const now = this.env.now();
    probe?.recordInputSend(now);
    this.sendRaw(this.codec.encodeInputPacket(lastSnapshotTick, cmds), now, "input");
  }

  /** The game reports every reconciliation that moved the player (net graph). */
  recordCorrection(meters: number, nowMs: number = this.env.now()): void {
    this.corrections.record(nowMs);
    this.stats.lastCorrectionM = meters;
    this.stats.correctionsPerMin = this.corrections.perMinute(nowMs);
  }

  // ---- receiving ----

  private receiveReliable(payload: Uint8Array): void {
    const now = this.env.now();
    this.lastRecvMs = now;
    this.rateIn.add(payload.length + 4, now);
    let msg: ServerMessage;
    try {
      msg = fromBinary(ServerMessageSchema, payload);
    } catch {
      this.malformed++;
      return;
    }
    if (msg.msg.case === "kicked") this.kickReason = msg.msg.value.reason || "Kicked from the match";
    if (this.messageHandler) {
      this.messageHandler(msg);
    } else if (this.pendingMessages.length < MAX_PENDING_MESSAGES) {
      this.pendingMessages.push(msg);
    }
  }

  private receiveUnreliable(bytes: Uint8Array): void {
    const now = this.env.now();
    this.lastRecvMs = now;
    this.rateIn.add(bytes.length, now);
    let decoded: DecodedUnreliable;
    try {
      decoded = this.codec.decode(bytes);
    } catch {
      this.malformed++;
      return;
    }
    if (decoded.kind === "pong") {
      const p = decoded.pong;
      this.clock.onPong(now, p.clientTimeMs, p.serverTick, p.tickPhase);
      this.stats.pingMs = this.clock.rttMs();
      this.stats.jitterMs = this.clock.jitterMs();
      return;
    }
    const s = decoded.snapshot;
    this.loss.record(s.tick);
    this.stats.lossPct = this.loss.percent();
    if (s.inputSlack !== null) {
      this.leads[this.leadNext] = (s.inputSlack / SLACK_PER_TICK) * TICK_MS;
      this.leadNext = (this.leadNext + 1) % LEAD_WINDOW;
      if (this.leadCount < LEAD_WINDOW) this.leadCount++;
      let sum = 0;
      for (let i = 0; i < this.leadCount; i++) sum += this.leads[i] as number;
      this.stats.inputLeadMs = sum / this.leadCount;
    }

    if (this.snapshotHandler) {
      this.snapshotHandler(s, now);
    } else {
      this.pendingSnapshots.push({ s, arrivalMs: now });
      if (this.pendingSnapshots.length > MAX_PENDING_SNAPSHOTS) this.pendingSnapshots.shift();
    }
  }
}
